import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getApplyToken,
  submitApplication,
  type ApplyResult,
} from '../lib/membership';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMPTY = {
  fullName: '',
  email: '',
  message: '',
  howHeard: '',
  valuesAffirmed: false,
  dataConsent: false,
  website: '', // honeypot
};

/**
 * Membership application form (SC-001/002/003). Four states: idle → submitting (disabled + spinner,
 * bounded by the client timeout) → success (this form is replaced by an inline panel; focus moves to
 * it) OR error (specific per-field messages, values preserved). No "empty" state — the form always
 * renders. WCAG 2.1 AA: labelled fields, aria-invalid + aria-describedby on errors, aria-live status,
 * status conveyed by icon + text (never colour alone). Anti-bot: a server nonce fetched on mount + a
 * hidden honeypot; neither is visible to a real applicant.
 */
export function ApplicationForm() {
  const [values, setValues] = useState({ ...EMPTY });
  const [status, setStatus] = useState<Status>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [emailQueued, setEmailQueued] = useState(true);
  const nonceRef = useRef<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const errorSummaryRef = useRef<HTMLParagraphElement>(null);

  // Fetch the anti-bot timing nonce on mount (research D1). A missing nonce is handled on submit.
  useEffect(() => {
    const ac = new AbortController();
    getApplyToken(ac.signal).then((t) => {
      nonceRef.current = t;
    });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  function set<K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    setFieldErrors({});
    setGeneralError(null);
    setStatus('submitting');

    // Ensure we have a nonce (refetch once if the mount fetch failed).
    let nonce = nonceRef.current;
    if (!nonce) nonce = await getApplyToken();
    if (!nonce) {
      setGeneralError('We couldn’t reach the server. Please check your connection and try again.');
      setStatus('error');
      return;
    }

    const result: ApplyResult = await submitApplication({
      fullName: values.fullName,
      email: values.email,
      message: values.message || undefined,
      howHeard: values.howHeard || undefined,
      valuesAffirmed: values.valuesAffirmed,
      dataConsent: values.dataConsent,
      nonce,
      website: values.website,
    });
    // A nonce is single-use; drop it so a resubmit fetches a fresh one.
    nonceRef.current = null;

    if (result.kind === 'ok') {
      setEmailQueued(result.emailQueued);
      setStatus('success');
      return;
    }
    if (result.kind === 'invalid') {
      setFieldErrors(result.errors);
      setStatus('error');
      queueMicrotask(() => errorSummaryRef.current?.focus());
      return;
    }
    if (result.kind === 'rate_limited') {
      setGeneralError('Too many attempts from your network. Please wait a little while and try again.');
    } else {
      setGeneralError('Something went wrong sending your application. Please try again in a moment.');
    }
    setStatus('error');
    queueMicrotask(() => errorSummaryRef.current?.focus());
  }

  if (status === 'success') {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        className="rounded-lg border border-border bg-surface p-8 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 aria-hidden className="mt-0.5 h-6 w-6 shrink-0 text-success" />
          <div className="space-y-3">
            <h2 className="font-display text-lg font-semibold uppercase tracking-[0.12em] text-ink">
              Application received
            </h2>
            <p className="font-serif text-lg text-ink">
              Thank you. The board will review your application. Membership is free, so there’s nothing to
              pay and nothing more to do right now.
            </p>
            <p className="font-sans text-sm text-muted">
              {emailQueued
                ? 'A confirmation is on its way to your email, with a link you can use to withdraw at any time.'
                : 'Your confirmation email may be delayed, but your application is safely recorded.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const submitting = status === 'submitting';

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      {generalError && (
        <p
          ref={errorSummaryRef}
          tabIndex={-1}
          role="alert"
          className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-4 py-3 font-sans text-sm text-ink"
        >
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-error" />
          <span>{generalError}</span>
        </p>
      )}

      <TextField
        id="fullName"
        label="Full name"
        value={values.fullName}
        onChange={(v) => set('fullName', v)}
        error={fieldErrors.fullName}
        autoComplete="name"
        required
        disabled={submitting}
      />
      <TextField
        id="email"
        label="Email"
        type="email"
        value={values.email}
        onChange={(v) => set('email', v)}
        error={fieldErrors.email}
        autoComplete="email"
        required
        disabled={submitting}
      />
      <TextAreaField
        id="message"
        label="Why you’d like to join"
        hint="Optional — a sentence or two helps the board know you."
        value={values.message}
        onChange={(v) => set('message', v)}
        error={fieldErrors.message}
        disabled={submitting}
      />
      <TextField
        id="howHeard"
        label="How did you hear about us?"
        hint="Optional."
        value={values.howHeard}
        onChange={(v) => set('howHeard', v)}
        error={fieldErrors.howHeard}
        disabled={submitting}
      />

      {/* Honeypot — visually hidden, off the tab order, ignored by assistive tech. A real user never
          fills it; a bot that does is silently dropped server-side. */}
      <div aria-hidden className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="website">Leave this field empty</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => set('website', e.target.value)}
        />
      </div>

      <CheckboxField
        id="valuesAffirmed"
        checked={values.valuesAffirmed}
        onChange={(c) => set('valuesAffirmed', c)}
        error={fieldErrors.valuesAffirmed}
        disabled={submitting}
      >
        I share the association’s{' '}
        <Link to="/principles" className="text-accent underline underline-offset-2 hover:text-accent-deep">
          values
        </Link>
        .
      </CheckboxField>

      <CheckboxField
        id="dataConsent"
        checked={values.dataConsent}
        onChange={(c) => set('dataConsent', c)}
        error={fieldErrors.dataConsent}
        disabled={submitting}
      >
        I consent to my name and email being stored to process this application. We store only what you
        enter here, use it only to review your application, and you can withdraw at any time from the link
        in your confirmation email.
      </CheckboxField>

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 rounded-md border border-accent bg-accent px-5 py-2.5 font-sans text-sm font-semibold text-primary-deep transition-colors duration-150 ease-standard hover:bg-accent-deep hover:text-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
        {submitting ? 'Sending…' : 'Send application'}
      </button>
      <p className="sr-only" aria-live="polite">
        {submitting ? 'Sending your application.' : ''}
      </p>
    </form>
  );
}

/* ---- field primitives (hand-rolled Tailwind, mirroring HealthBadge conventions) ---- */

const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60';

function FieldError({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={id} className="flex items-center gap-1.5 font-sans text-xs text-error">
      <TriangleAlert aria-hidden className="h-3.5 w-3.5" />
      {error}
    </p>
  );
}

function TextField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const errId = `${props.id}-error`;
  const hintId = `${props.id}-hint`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="block font-sans text-sm font-medium text-ink">
        {props.label}
        {props.required && <span className="text-error"> *</span>}
      </label>
      {props.hint && (
        <p id={hintId} className="font-sans text-xs text-muted">
          {props.hint}
        </p>
      )}
      <input
        id={props.id}
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        disabled={props.disabled}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={[props.hint ? hintId : null, props.error ? errId : null].filter(Boolean).join(' ') || undefined}
        className={inputClass}
      />
      <FieldError id={errId} error={props.error} />
    </div>
  );
}

function TextAreaField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const errId = `${props.id}-error`;
  const hintId = `${props.id}-hint`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="block font-sans text-sm font-medium text-ink">
        {props.label}
      </label>
      {props.hint && (
        <p id={hintId} className="font-sans text-xs text-muted">
          {props.hint}
        </p>
      )}
      <textarea
        id={props.id}
        rows={4}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        disabled={props.disabled}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={[props.hint ? hintId : null, props.error ? errId : null].filter(Boolean).join(' ') || undefined}
        className={inputClass}
      />
      <FieldError id={errId} error={props.error} />
    </div>
  );
}

function CheckboxField(props: {
  id: string;
  checked: boolean;
  onChange: (c: boolean) => void;
  error?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const errId = `${props.id}-error`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={props.id} className="flex items-start gap-3 font-sans text-sm text-ink">
        <input
          id={props.id}
          type="checkbox"
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
          disabled={props.disabled}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={props.error ? errId : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--color-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
        <span>{props.children}</span>
      </label>
      <FieldError id={errId} error={props.error} />
    </div>
  );
}
